import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import dayjs from "dayjs";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "../../../authentication/backendModule/admin/auth";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { parseValidationErrors } from "../../../apiHelper/validationHelper";
import Export from "../../../formHelper/export";
import Search from "../../../formHelper/search";
import Pagination from "../../../formHelper/pagination";
import Table from "../../../formHelper/list";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Success from "../../../formHelper/success";
import Delete from "../../../formHelper/delete";
import exportApiCall from "../../../apiHelper/export";
import FilterDropdown from "../../../formHelper/filterDropDown";
import Loader from "../../../formHelper/Loader";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
interface UserRow {
  id: number;
  name: string;
  last_name: string;
  user_name: string;
  cc_email: string;
  password: string;
  passwordChanged?: boolean;
  role_id: number;
  branch?: { id: number; name: string };
  created_at?: string;
}

interface KV {
  id: number;
  name: string;
}

interface PermissionList {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const User: React.FC = () => {
  const backendRoute = all_routes.backend;

  // data
  const [list, setList] = useState<UserRow[]>([]);
  const [branchList, setBranchList] = useState<KV[]>([]);
  const [roleList, setRoleList] = useState<KV[]>([]);

  // form
  const [formData, setFormData] = useState<any>({
    name: "",
    last_name: "",
    user_name: "",
    email: "",
    password: "",
    branch_id: "",
    role_id: "",
    cc_email: "",
    passwordChanged: false,
  });
  const [formError, setFormError] = useState<{ [k: string]: string }>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  // UI
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // list + filters
  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() =>
    typeof window !== "undefined" && window.innerWidth <= 768 ? 5 : 10
  );
  const [totalItems, setTotalItems] = useState(0);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // export/polling
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // guards
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const ROLE_OPTIONS = [
    { value: "sales", label: "Sales" },
    { value: "service", label: "Service" },
  ];

  const toOptions = (arr: KV[]) =>
    arr.map((x) => ({ value: x.id, label: x.name }));

  /* -------------------- Helpers: download/poll -------------------- */
  // ---------- export helpers ----------
  const triggerDownload = (url: string, filename: string, revoke = false) => {
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setDownloading(true);
    setTimeout(() => setDownloading(false), 3000);
    setExporting(false);
  };

  // Poll API for local download
  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });

        if (res) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename, true);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, 5000);
  };

  /* -------------------- Export -------------------- */
  const exportFile = async () => {
    if (exporting || submitting || deleting) return;
    try {
      setExporting(true);
      setDownloading(false);

      const result = await apiRequestCall("post", backendRoute.getUser, {
        data: {
          courier_name: query,
          start_date: startDate,
          end_date: endDate,
          branch_list: branchFilter,
          download: true,
        },
      });

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      pollForFile(filename);
    } catch (err) {
      console.error("Export request failed:", err);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  };

  /* -------------------- Form handlers -------------------- */
  const inputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormError((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      last_name: "",
      user_name: "",
      email: "",
      password: "",
      branch_id: "",
      role_id: "",
      cc_email: "",
      passwordChanged: false,
    });
    setFormError({});
    setSelectedId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        user_id: selectedId,
        update_status: !!selectedId,
      };

      const result = await apiRequestCall("post", backendRoute.addUpdateUser, {
        data: payload,
      });

      if (result?.data?.code === 221) {
        setFormError(parseValidationErrors(result?.data?.errors));
        return;
      }

      if (result?.data?.code === 200) {
        resetForm();
        setShowSuccess(true);
        setShowMessage(result?.data?.message);
        fetchList({ page: currentPage, perPage });
      } else {
        setShowMessage(result?.data?.message || "Operation failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Submit failed:", err);
      setShowMessage((err as any)?.message || "Submit failed. Please try.");
      setShowSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const updateRecord = (row: UserRow) => {
    const selected = list.find((x) => x.id === row.id);
    if (!selected) return;
    setFormError({});
    setFormData({
      ...selected,
      password: "********",
      passwordChanged: false,
    });

    setSelectedId(selected.id);
    setShowForm(true);
  };

  /* -------------------- Delete -------------------- */
  const deleteRecord = (id: number) => {
    if (submitting || deleting) return;
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteUser, {
        data: { id: deleteId },
      });
      if (result?.data?.code === 200) {
        fetchList({ page: currentPage, perPage });
        setShowMessage(result?.data?.message);
        setShowSuccess(true);
      } else {
        setShowMessage(result?.data?.message || "Delete failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed. Please try again.");
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeleting(false);
    }
  };

  /* -------------------- Fetch list & lookups -------------------- */
  const fetchList = async ({
    page = 1,
    perPage = 10,
  }: { page?: number; perPage?: number } = {}) => {
    setLoading(true);
    try {
      const result = await apiRequestCall("post", backendRoute.getUser, {
        data: {
          page,
          per_page: perPage,
          start_date: startDate,
          end_date: endDate,
          search: query.trim(),
          branch_list: branchFilter !== null ? branchFilter : [],
        },
      });

      const res = result?.data?.data;
      if (res) {
        setList(res.data ?? []);
        setCurrentPage(res.current_page ?? 1);
        setPerPage(res.per_page ?? 10);
        setTotalItems(res.total ?? 0);
      } else {
        setList([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setList([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const getLookup = async (endpoint: string) => {
    try {
      const r = await apiRequestCall("post", endpoint);
      const raw = r?.data?.data;
      if (raw && typeof raw === "object") {
        return Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${endpoint}`, err);
    }
    return [] as KV[];
  };

  const loadLookups = async () => {
    const [branches, roles] = await Promise.all([
      getLookup(backendRoute.getBranchDD),
      getLookup(backendRoute.getRoleDD),
    ]);
    setBranchList(branches);
    setRoleList(roles);
  };

  useEffect(() => {
    fetchList({ page: currentPage, perPage });
  }, [query, currentPage, perPage, startDate, endDate, branchFilter]);

  useEffect(() => {
    loadLookups();
  }, []);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as PermissionList | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;

  /* -------------------- Table columns -------------------- */
  const columns = [
    {
      key: "id",
      label: "Sr. No",
      render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
    },
    { key: "name", label: "First Name" },
    { key: "last_name", label: "Last Name" },
    { key: "user_name", label: "Username" },
    { key: "password", label: "Password" },
    { key: "email", label: "Email Id" },
    { key: "branch.name", label: "Branch" },
    {
      key: "created_at",
      label: "Date",
    },

    ...(permissionMap?.user?.edit_user || permissionMap?.user?.delete_user
      ? [
          {
            key: "action",
            label: "Action",
            render: (row: UserRow) => (
              <div className="hstack gap-2 fs-15">
                {permissionMap?.user?.edit_user && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-info"
                    onClick={() => updateRecord(row)}
                    disabled={submitting || deleting}
                    title="Edit Role"
                    aria-label={`Edit User ${row.name}`}
                  >
                    <i className="feather-edit" />
                  </button>
                )}

                {permissionMap?.user?.delete_user && (
                  <button
                    className="btn btn-icon btn-sm btn-soft-danger"
                    onClick={() => deleteRecord(row.id)}
                    disabled={submitting || deleting}
                    title="Delete Role"
                    aria-label={`Delete User ${row.name}`}
                  >
                    <i className="feather-trash" />
                  </button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  const perPageOptions = [
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 25, label: "25" },
  ];

  /* -------------------- Render (source.tsx layout: controls card + table card) -------------------- */
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header: Title | (mobile export) | New */}
        <div className="row page-header">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Users
                  <span className="count-title">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex justify-content-end gap-2">
                  {permissionMap?.["user"]?.["add_user"] && (
                    <Export
                      options={[
                        {
                          label: downloading
                            ? "Downloading..."
                            : exporting
                            ? "Generating..."
                            : "Export",
                          iconClass: downloading
                            ? "ti ti-download"
                            : exporting
                            ? "ti ti-loader"
                            : "ti ti-file-type-xls",
                          colorClass:
                            exporting || downloading
                              ? "text-muted"
                              : "text-primary",
                          onClick:
                            exporting || downloading || submitting || deleting
                              ? undefined
                              : exportFile,
                        },
                      ]}
                    />
                  )}
                  {permissionMap?.["user"]?.["add_user"] && (
                    <button
                      className="btn btn-soft-success d-flex align-items-center"
                      onClick={() => {
                        resetForm();
                        setShowForm(true);
                      }}
                      disabled={submitting}
                      aria-label="Add new owner"
                    >
                      <i className="ti ti-square-rounded-plus" />
                      <span className="d-none d-md-inline ms-1">New</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top card: search / date / filter */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search Owner"
                    value={query}
                    onChange={(v) => {
                      setQuery(v);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="table-responsive">
                <Table<UserRow> data={list} columns={columns} />
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(p) => {
              setCurrentPage(p);
            }}
            onPerPageChange={(n) => {
              setPerPage(n);
              setCurrentPage(1);
            }}
            perPageOptions={perPageOptions}
          />
        </div>

        {/* Offcanvas Form */}
        <div
          className={`offcanvas offcanvas-end offcanvas-large ${
            showForm ? "show" : ""
          }`}
          style={{ visibility: showForm ? "visible" : "hidden" }}
          tabIndex={-1}
          role="dialog"
        >
          <div className="offcanvas-header border-bottom">
            <h5 className="fw-semibold">
              {selectedId ? "Update User" : "Add User"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowForm(false)}
              disabled={submitting}
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="offcanvas-body">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!submitting) handleSubmit();
              }}
            >
              {/* Names */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      First Name <span className="text-danger">*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={inputChange}
                      disabled={submitting}
                    />
                    {formError.name && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Last Name <span className="text-danger">*</span>
                    </label>
                    <input
                      name="last_name"
                      type="text"
                      className="form-control"
                      value={formData.last_name}
                      onChange={inputChange}
                      disabled={submitting}
                    />
                    {formError.last_name && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.last_name}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Username / Password */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Username <span className="text-danger">*</span>
                    </label>
                    <input
                      name="user_name"
                      type="text"
                      className="form-control"
                      value={formData.user_name}
                      onChange={inputChange}
                      disabled={submitting}
                    />
                    {formError.user_name && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.user_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Password <span className="text-danger">*</span>
                    </label>
                    <input
                      name="password"
                      type="password"
                      className="form-control"
                      value={
                        selectedId && !formData.passwordChanged
                          ? "********"
                          : formData.password || ""
                      }
                      onChange={(e) => {
                        if (selectedId && !formData.passwordChanged) {
                          setFormData((prev: any) => ({
                            ...prev,
                            password: "",
                            passwordChanged: true,
                          }));
                        } else inputChange(e);
                      }}
                      disabled={submitting}
                    />
                    {formError.password && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.password}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Email / Branch */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Email Id <span className="text-danger">*</span>
                    </label>
                    <input
                      name="email"
                      type="text"
                      className="form-control"
                      value={formData.email}
                      onChange={inputChange}
                      disabled={submitting}
                    />
                    {formError.email && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.email}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      Branch <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="branch_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={toOptions(branchList)}
                      value={
                        toOptions(branchList).find(
                          (o) => o.value === formData.branch_id
                        ) || null
                      }
                      onChange={(opt) => {
                        setFormError((p) => {
                          const q = { ...p };
                          delete q.branch_id;
                          return q;
                        });
                        setFormData((prev: any) => ({
                          ...prev,
                          branch_id: opt?.value || null,
                        }));
                      }}
                      isClearable
                      isDisabled={submitting}
                    />
                    {formError.branch_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.branch_id}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* CC Email / Role */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="col-form-label">
                      CC Email <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="cc_email"
                      className="form-control"
                      style={{ height: "125px" }}
                      value={formData.cc_email}
                      onChange={inputChange}
                      disabled={submitting}
                    />
                    {formError.cc_email && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.cc_email}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="col-form-label">
                    Assign Role <span className="text-danger">*</span>
                  </label>
                  <div className="mb-3">
                    <Select
                      placeholder="Select Role"
                      name="role_id"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={toOptions(roleList)}
                      value={
                        toOptions(roleList).find(
                          (o) => o.value === formData.role_id
                        ) || null
                      }
                      onChange={(opt) => {
                        setFormError((p) => {
                          const q = { ...p };
                          delete q.role_id;
                          return q;
                        });
                        setFormData((prev: any) => ({
                          ...prev,
                          role_id: opt?.value || null,
                        }));
                      }}
                      isClearable
                      isDisabled={submitting}
                    />
                    {formError.role_id && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.role_id}
                      </div>
                    )}
                  </div>
                  <label className="col-form-label">
                    Select Team (Optional)
                  </label>
                  <div className="mb-3">
                    <Select
                      placeholder="Select Team"
                      name="team_type"
                      classNamePrefix="react-select"
                      className="tagging"
                      options={ROLE_OPTIONS}
                      value={
                        ROLE_OPTIONS.find(
                          (o) => o.value === formData.team_type
                        ) || null
                      }
                      onChange={(opt) => {
                        setFormError((prev) => {
                          const next = { ...prev };
                          delete next.team_type;
                          return next;
                        });
                        setFormData((prev: any) => ({
                          ...prev,
                          team_type: opt?.value || "",
                        }));
                      }}
                      isClearable
                      isDisabled={submitting}
                    />
                    {formError.team_type && (
                      <div className="invalid-feedback d-block text-danger">
                        {formError.team_type}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form buttons */}
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-soft-primary me-2"
                  onClick={() => setShowForm(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-soft-success"
                  disabled={submitting}
                >
                  {selectedId
                    ? submitting
                      ? "Updating..."
                      : "Update"
                    : submitting
                    ? "Creating..."
                    : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <Success
          id="formatSuccessModal"
          title="User"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete User?"
          message="Are you sure?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default User;
